import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  AlertCircle, 
  AlertTriangle, 
  Info, 
  TrendingDown,
  Package,
  Clock,
  RefreshCw,
  ExternalLink,
  Eye,
  EyeOff
} from 'lucide-react';
import { MOCK_ALERTS } from '@/lib/adapters/mockData';
import type { Alert } from '@/types/client';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

interface AlertPanelProps {
  clientId: string;
}

const ALERT_CONFIG = {
  buybox_loss: {
    icon: TrendingDown,
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    iconColor: 'text-orange-600',
  },
  stock_low: {
    icon: Package,
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    iconColor: 'text-red-600',
  },
  acos_high: {
    icon: AlertTriangle,
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    iconColor: 'text-yellow-600',
  },
  returns_high: {
    icon: RefreshCw,
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    iconColor: 'text-blue-600',
  },
  delivery_delay: {
    icon: Clock,
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    iconColor: 'text-purple-600',
  },
};

const SEVERITY_CONFIG = {
  low: { color: 'bg-gray-500', label: 'Faible' },
  medium: { color: 'bg-yellow-500', label: 'Moyen' },
  high: { color: 'bg-orange-500', label: 'Élevé' },
  critical: { color: 'bg-red-500', label: 'Critique' },
};

export function AlertPanel({ clientId }: AlertPanelProps) {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRead, setShowRead] = useState(false);
  const [readAlerts, setReadAlerts] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setAlerts(MOCK_ALERTS);
      setLoading(false);
    }, 500);
  }, [clientId]);

  const markAsRead = (alertId: string) => {
    setReadAlerts(prev => new Set([...prev, alertId]));
  };

  const filteredAlerts = alerts.filter(alert => 
    showRead ? true : !readAlerts.has(alert.id)
  );

  const criticalCount = alerts.filter(a => a.severity === 'critical' && !readAlerts.has(a.id)).length;
  const highCount = alerts.filter(a => a.severity === 'high' && !readAlerts.has(a.id)).length;

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <AlertCircle className="mr-2 h-5 w-5" />
            Alertes intelligentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-16 bg-gray-100 rounded-lg"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-fit">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center text-lg">
            <AlertCircle className="mr-2 h-5 w-5" />
            Alertes intelligentes
          </CardTitle>
          <div className="flex items-center space-x-1">
            {criticalCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                {criticalCount} critique{criticalCount > 1 ? 's' : ''}
              </Badge>
            )}
            {highCount > 0 && (
              <Badge className="bg-orange-500 text-white text-xs">
                {highCount} élevée{highCount > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        </div>
        <CardDescription>
          Surveillance automatique des performances
        </CardDescription>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="flex items-center justify-between mb-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowRead(!showRead)}
            className="text-xs"
          >
            {showRead ? (
              <>
                <EyeOff className="mr-1 h-3 w-3" />
                Masquer lues
              </>
            ) : (
              <>
                <Eye className="mr-1 h-3 w-3" />
                Afficher lues
              </>
            )}
          </Button>
          <span className="text-xs text-muted-foreground">
            {filteredAlerts.length} alerte{filteredAlerts.length > 1 ? 's' : ''}
          </span>
        </div>

        <ScrollArea className="h-[400px]">
          <div className="space-y-3">
            {filteredAlerts.map((alert) => {
              const config = ALERT_CONFIG[alert.type];
              const severityConfig = SEVERITY_CONFIG[alert.severity];
              const Icon = config.icon;
              const isRead = readAlerts.has(alert.id);

              return (
                <div
                  key={alert.id}
                  className={`
                    p-3 rounded-lg border transition-all duration-200 hover:shadow-sm cursor-pointer
                    ${isRead ? 'opacity-60' : ''}
                    ${config.bgColor} ${config.borderColor}
                  `}
                  onClick={() => markAsRead(alert.id)}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`
                      p-2 rounded-full ${config.bgColor}
                      ${config.iconColor}
                    `}>
                      <Icon className="h-4 w-4" />
                    </div>

                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium leading-none">
                          {alert.title}
                        </h4>
                        <div className={`
                          w-2 h-2 rounded-full ${severityConfig.color}
                        `} />
                      </div>

                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {alert.description}
                      </p>

                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(alert.created_at), {
                            addSuffix: true,
                            locale: fr,
                          })}
                        </span>

                        {alert.actionUrl && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Navigate to action URL
                              console.log('Navigate to:', alert.actionUrl);
                            }}
                          >
                            <ExternalLink className="mr-1 h-3 w-3" />
                            Voir
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredAlerts.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Info className="mx-auto h-8 w-8 mb-2 opacity-50" />
                <p className="text-sm">
                  {showRead ? 'Aucune alerte' : 'Aucune nouvelle alerte'}
                </p>
                <p className="text-xs mt-1">
                  Tout semble bien fonctionner !
                </p>
              </div>
            )}
          </div>
        </ScrollArea>

        {alerts.length > 0 && (
          <div className="mt-4 pt-3 border-t">
            <Button
              variant="outline"
              size="sm"
              className="w-full text-xs"
              onClick={() => setReadAlerts(new Set(alerts.map(a => a.id)))}
            >
              Marquer tout comme lu
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}